/**
 * URL Authentication Utilities
 * Extracts and processes authentication tokens from URL parameters
 * Maintains 100% compatibility with HtmlChart implementation
 */

/**
 * Detect broker from hostname (redirect destination)
 * The subdomain prefix determines which broker infrastructure to use:
 * - x.tradezone.com.br → XPI (all XPI/TZ/CLEAR users redirect here)
 * - r.tradezone.com.br → RICO
 * - tv.tradezone.com.br → RICO (TradingView entry point, shows chart selection)
 *
 * @param {string} hostname - Current window.location.hostname
 * @returns {string|null} Broker code or null if not detected
 */
function detectBrokerFromHostname(hostname) {
  // RICO users are redirected to r.tradezone.com.br or tv.tradezone.com.br
  if (hostname === "r.tradezone.com.br" || hostname === "tv.tradezone.com.br") {
    return "RICO";
  }

  // XPI users are redirected to x.tradezone.com.br
  // (TZ and CLEAR also redirect here, but those are legacy/rare cases)
  if (hostname === "x.tradezone.com.br") {
    return "XPI";
  }

  return null;
}

/**
 * Get broker suffix for legacy chart URLs
 * @param {string} broker - Broker code (XPI, RICO, CLEAR, TZ)
 * @returns {string} Suffix to append to auth token
 */
function getBrokerSuffix(broker) {
  const suffixMap = {
    XPI: "1exx",
    RICO: "9ddhr",
    CLEAR: "0dhac",
    TZ: "", // No suffix for TZ
  };
  return suffixMap[broker] || "";
}

/**
 * Extract auth token and broker from URL parameters
 * @returns {Object|null} { authToken, broker } or null if no auth param
 */
export function extractAuthFromUrl() {
  console.log("[urlAuth] ========== EXTRACTING AUTH FROM URL ==========");
  console.log("[urlAuth] Full URL:", window.location.href);
  console.log("[urlAuth] Search params:", window.location.search);

  const urlParams = new URLSearchParams(window.location.search);
  let authToken = urlParams.get("auth");

  console.log(
    "[urlAuth] Auth token found:",
    authToken ? `${authToken.substring(0, 20)}...` : "NO TOKEN IN URL",
  );

  if (!authToken) {
    console.log("[urlAuth] ❌ No auth token in URL, returning null");
    console.log("[urlAuth] ================================================");
    return null;
  }

  console.log(
    "[urlAuth] ✅ Auth token detected, proceeding with broker detection...",
  );

  // Extract RICO-specific parameters for TradingView redirect
  const auth2 = urlParams.get("auth2");
  const ricoAccountNumber = urlParams.get("ricoAccountNumber");
  const isOverturned = urlParams.get("isOverturned");

  if (auth2) sessionStorage.setItem("tradingview-auth2", auth2);
  if (ricoAccountNumber)
    sessionStorage.setItem("tradingview-account", ricoAccountNumber);
  if (isOverturned)
    sessionStorage.setItem("tradingview-overturned", isOverturned);

  // Step 1: Check hostname first (redirect destination determines broker)
  // x.tradezone.com.br → XPI
  // r.tradezone.com.br → RICO
  const hostname = window.location.hostname;
  let broker = detectBrokerFromHostname(hostname);

  console.log(
    `[urlAuth] Hostname: ${hostname}, Broker detected: ${broker || "none (checking token suffix)"}`,
  );

  // Step 2: Check token suffix as fallback (for legacy/rare cases)
  // Only check if hostname didn't determine broker
  let tokenSuffix = null;
  let tokenHasSuffix = false;

  if (!broker) {
    const clearIndex = authToken.lastIndexOf("0dhac");
    const ricoIndex = authToken.lastIndexOf("9ddhr");
    const xpiIndex = authToken.lastIndexOf("1exx");

    if (clearIndex !== -1) {
      broker = "CLEAR";
      tokenSuffix = clearIndex;
      tokenHasSuffix = true;
    } else if (ricoIndex !== -1) {
      broker = "RICO";
      tokenSuffix = ricoIndex;
      tokenHasSuffix = true;
    } else if (xpiIndex !== -1) {
      broker = "XPI";
      tokenSuffix = xpiIndex;
      tokenHasSuffix = true;
    }
  } else {
    // Hostname determined broker, but check if token has suffix to remove it
    const clearIndex = authToken.lastIndexOf("0dhac");
    const ricoIndex = authToken.lastIndexOf("9ddhr");
    const xpiIndex = authToken.lastIndexOf("1exx");

    if (clearIndex !== -1 || ricoIndex !== -1 || xpiIndex !== -1) {
      tokenSuffix = Math.max(clearIndex, ricoIndex, xpiIndex);
      tokenHasSuffix = true;
    }
  }

  // Step 3: Default to TZ if still not determined (rare case)
  if (!broker) {
    broker = "TZ";
    console.log(
      `[urlAuth] No hostname or token suffix found, defaulting to TZ`,
    );
  }

  // Remove suffix from token if present (HtmlChart main.js:750-752)
  let cleanToken = authToken;
  if (tokenSuffix !== null) {
    cleanToken = authToken.substring(0, tokenSuffix);
  }

  // Step 4: Build token with correct suffix for legacy charts
  const suffix = getBrokerSuffix(broker);
  const tokenWithSuffix = cleanToken + suffix;

  // IMPORTANT: Store token WITH SUFFIX for legacy chart redirects
  // This ensures ChartSelectionModal can redirect to old charts with proper broker identification
  sessionStorage.setItem("original-auth-token", tokenWithSuffix);

  console.log(
    `[urlAuth] Final broker: ${broker} (from hostname: ${hostname}, token had suffix: ${tokenHasSuffix})`,
  );
  console.log(
    `[urlAuth] Token with ${suffix || "no"} suffix (${suffix}) stored for legacy charts`,
  );
  if (auth2)
    console.log(
      `[urlAuth] TradingView parameters stored (auth2, account, overturned)`,
    );

  return { authToken: cleanToken, broker };
}

/**
 * Clear auth parameter from URL without reloading page
 * Prevents auth token from appearing in browser history
 */
export function clearAuthFromUrl() {
  const url = new URL(window.location);
  url.searchParams.delete("auth");
  url.searchParams.delete("auth2");
  url.searchParams.delete("ricoAccountNumber");
  url.searchParams.delete("isOverturned");
  url.searchParams.delete("force");
  window.history.replaceState({}, "", url);
  console.log(`[urlAuth] Auth parameters cleared from URL`);
}
